import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebaseConfig'; 
import { collection, addDoc, deleteDoc, doc, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { 
    FaPaperPlane, FaClock, FaCalendarCheck, FaTrashAlt, FaLink, FaCopy, 
    FaSearch, FaFilter, FaSmileBeam, FaMeh, FaFrown, FaSadTear, FaGrinStars, 
    FaChevronDown, FaChevronUp, FaCalendarAlt, FaRedo
} from 'react-icons/fa';
import '../../styles/MoodManagement.css'; 

const MOOD_CONFIG = {
    'muito-feliz': { label: 'Muito Feliz', icon: <FaGrinStars />, color: '#27ae60', bg: '#eafaf1' },
    'feliz':       { label: 'Feliz',       icon: <FaSmileBeam />, color: '#2980b9', bg: '#ebf5fb' },
    'neutro':      { label: 'Neutro',      icon: <FaMeh />,       color: '#f39c12', bg: '#fef9e7' },
    'triste':      { label: 'Triste',      icon: <FaFrown />,     color: '#e67e22', bg: '#fdf2e9' },
    'muito-triste':{ label: 'Muito Triste',icon: <FaSadTear />,   color: '#c0392b', bg: '#fdedec' },
};

// --- PAGINAÇÃO ATUALIZADA ---
const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange, totalItems }) => {
    if (totalItems === 0) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
            pages.push(i);
        } else if (pages[pages.length - 1] !== '...') {
            pages.push('...');
        }
    }

    return (
        <div className="mood-manager-pagination">
            <div className="mood-manager-page-info">
                <span>Exibir</span>
                <select 
                    value={itemsPerPage} 
                    onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                    className="mood-manager-select-sm"
                >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                </select>
                <span>de {totalItems} resultados</span>
            </div>
            <div className="mood-manager-page-controls">
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="mood-manager-page-btn">&lt;</button>
                {pages.map((p, i) => (
                    <button 
                        key={i} 
                        onClick={() => typeof p === 'number' && onPageChange(p)} 
                        className={`mood-manager-page-btn ${p === currentPage ? 'active' : ''}`} 
                        disabled={p === '...'}
                    >
                        {p}
                    </button>
                ))}
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="mood-manager-page-btn">&gt;</button>
            </div>
        </div>
    );
};

const MoodManagement = ({ userData }) => {
    const [responses, setResponses] = useState([]);
    const [recurrenceRules, setRecurrenceRules] = useState([]);
    const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMood, setFilterMood] = useState('');

    const [showScheduler, setShowScheduler] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [panelMessage, setPanelMessage] = useState(null);
    
    const [isRecurrent, setIsRecurrent] = useState(false);
    const [frequency, setFrequency] = useState('weekly');
    const [dayOfWeek, setDayOfWeek] = useState('1');
    const [dayOfMonth, setDayOfMonth] = useState('1');
    const [time, setTime] = useState('09:00');
    const [scheduledDateTime, setScheduledDateTime] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const empresaId = userData?.empresa_id;
    const publicLink = `${window.location.origin}/pesquisa-humor?empresaId=${empresaId}`;

    useEffect(() => {
        if (!empresaId) return;
        const unsubRes = onSnapshot(query(collection(db, 'Empresas', empresaId, 'moodResponses'), orderBy('createdAt', 'desc')), (s) => setResponses(s.docs.map(d => ({id: d.id, ...d.data()}))));
        const unsubRules = onSnapshot(query(collection(db, 'Empresas', empresaId, 'recurrenceRules'), where('isActive', '==', true), orderBy('createdAt', 'desc')), (s) => setRecurrenceRules(s.docs.map(d => ({id: d.id, ...d.data()}))));
        const unsubCamp = onSnapshot(query(collection(db, 'Empresas', empresaId, 'moodSurveyCampaigns'), where('status', '==', 'scheduled'), orderBy('scheduledFor', 'asc')), (s) => setScheduledCampaigns(s.docs.map(d => ({id: d.id, ...d.data()}))));
        return () => { unsubRes(); unsubRules(); unsubCamp(); };
    }, [empresaId]);

    const filteredResponses = useMemo(() => {
        return responses.filter(res => {
            const matchesSearch = 
                (res.userName && res.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (res.observacao && res.observacao.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesMood = filterMood === '' || res.humor === filterMood;
            return matchesSearch && matchesMood;
        });
    }, [responses, searchTerm, filterMood]);

    const totalItems = filteredResponses.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const currentItems = filteredResponses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleCopyLink = () => { navigator.clipboard.writeText(publicLink); alert("Link copiado!"); };
    
    const createCampaign = async (campaignData) => {
        setIsSubmitting(true);
        try {
            await addDoc(collection(db, 'Empresas', empresaId, 'moodSurveyCampaigns'), { ...campaignData, createdBy: userData.id, createdAt: Timestamp.now(), empresaId });
            setPanelMessage({ type: 'success', text: `Sucesso! Pesquisa ${campaignData.status === 'active' ? 'enviada' : 'agendada'}.` });
            setShowScheduler(false);
        } catch (err) { setPanelMessage({ type: 'error', text: err.message }); } finally { setIsSubmitting(false); setTimeout(()=>setPanelMessage(null), 3000); }
    };

    const handleSaveRule = async () => {
        setIsSubmitting(true);
        try {
            const ruleData = { frequency, time, isActive: true, createdBy: userData.id, createdAt: Timestamp.now(), empresaId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
            if (frequency === 'weekly') ruleData.dayOfWeek = parseInt(dayOfWeek, 10);
            if (frequency === 'monthly') ruleData.dayOfMonth = parseInt(dayOfMonth, 10);
            await addDoc(collection(db, 'Empresas', empresaId, 'recurrenceRules'), ruleData);
            setPanelMessage({ type: 'success', text: 'Regra salva!' });
            setShowScheduler(false);
        } catch (err) { setPanelMessage({ type: 'error', text: err.message }); } finally { setIsSubmitting(false); setTimeout(()=>setPanelMessage(null), 3000); }
    };

    const deleteDocHandler = async (coll, id) => { if(window.confirm("Remover item?")) await deleteDoc(doc(db, 'Empresas', empresaId, coll, id)); };

    const formatRule = (r) => {
        const days = ["", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
        if (r.frequency === 'daily') return `Diariamente às ${r.time}`;
        if (r.frequency === 'weekly') return `Toda ${days[r.dayOfWeek]} às ${r.time}`;
        return `Dia ${r.dayOfMonth} às ${r.time}`;
    };

    return (
        <div className="mood-manager-container">
            {/* HEADER */}
            <div className="mood-manager-card">
                <div className="mood-manager-header-flex">
                    <div className="mood-manager-title-group">
                        <h3><FaSmileBeam /> Gestão de Humor & Clima</h3>
                        <p>Acompanhe o engajamento e automatize pesquisas.</p>
                    </div>
                    <div className="mood-manager-link-box">
                        <label className="mood-manager-link-label"><FaLink /> Link Público para Coleta</label>
                        <div className="mood-manager-link-row">
                            <input type="text" readOnly value={publicLink} />
                            <button onClick={handleCopyLink} title="Copiar"><FaCopy /> Copiar</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* SCHEDULER */}
            <div className="mood-manager-card">
                <div className="mood-manager-header-flex">
                    <div className="mood-manager-title-group">
                        <h3>Agendamentos & Regras</h3>
                        <p>Configure disparos automáticos para a equipe.</p>
                    </div>
                    <div className="mood-manager-actions">
                        <button className="mood-manager-btn outline" onClick={() => { if(window.confirm('Disparar agora?')) createCampaign({ status: 'active', scheduledFor: Timestamp.now() }); }}>
                            <FaPaperPlane /> Disparar Agora
                        </button>
                        <button className={`mood-manager-btn primary`} onClick={() => setShowScheduler(!showScheduler)}>
                            {showScheduler ? 'Fechar' : 'Novo Agendamento'} {showScheduler ? <FaChevronUp/> : <FaChevronDown/>}
                        </button>
                    </div>
                </div>

                {showScheduler && (
                    <div className="mood-manager-form-container">
                        <div className="mood-manager-tabs">
                            <button className={`mood-manager-tab ${!isRecurrent ? 'active' : ''}`} onClick={() => setIsRecurrent(false)}>
                                <FaCalendarAlt /> Agendamento Pontual
                            </button>
                            <button className={`mood-manager-tab ${isRecurrent ? 'active' : ''}`} onClick={() => setIsRecurrent(true)}>
                                <FaRedo /> Regra Recorrente
                            </button>
                        </div>

                        <div className="mood-manager-form-content">
                            {!isRecurrent ? (
                                <div className="mood-manager-form-grid">
                                    <div className="mood-manager-form-group full">
                                        <label>Data e Hora do Disparo</label>
                                        <input type="datetime-local" className="mood-manager-input" value={scheduledDateTime} onChange={e => setScheduledDateTime(e.target.value)} />
                                    </div>
                                </div>
                            ) : (
                                <div className="mood-manager-form-grid">
                                    <div className="mood-manager-form-group">
                                        <label>Frequência</label>
                                        <select className="mood-manager-select" value={frequency} onChange={e => setFrequency(e.target.value)}>
                                            <option value="daily">Diariamente</option>
                                            <option value="weekly">Semanalmente</option>
                                            <option value="monthly">Mensalmente</option>
                                        </select>
                                    </div>
                                    {frequency === 'weekly' && (
                                        <div className="mood-manager-form-group">
                                            <label>Dia da Semana</label>
                                            <select className="mood-manager-select" value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)}>
                                                <option value="1">Segunda-feira</option>
                                                <option value="2">Terça-feira</option>
                                                <option value="3">Quarta-feira</option>
                                                <option value="4">Quinta-feira</option>
                                                <option value="5">Sexta-feira</option>
                                            </select>
                                        </div>
                                    )}
                                    {frequency === 'monthly' && (
                                        <div className="mood-manager-form-group">
                                            <label>Dia do Mês</label>
                                            <input type="number" className="mood-manager-input" min="1" max="31" value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} />
                                        </div>
                                    )}
                                    <div className="mood-manager-form-group">
                                        <label>Horário</label>
                                        <input type="time" className="mood-manager-input" value={time} onChange={e => setTime(e.target.value)} />
                                    </div>
                                </div>
                            )}
                            
                            <div className="mood-manager-form-footer">
                                <button 
                                    className="mood-manager-btn success" 
                                    disabled={isSubmitting} 
                                    onClick={isRecurrent ? handleSaveRule : () => { 
                                        if(scheduledDateTime) createCampaign({ status: 'scheduled', scheduledFor: Timestamp.fromDate(new Date(scheduledDateTime)) });
                                        else alert("Preencha a data.");
                                    }}
                                >
                                    {isSubmitting ? 'Salvando...' : 'Confirmar Agendamento'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {panelMessage && <div className={`mood-manager-message ${panelMessage.type}`}>{panelMessage.text}</div>}

                {(recurrenceRules.length > 0 || scheduledCampaigns.length > 0) && (
                    <div className="mood-manager-rules-grid">
                        <div className="mood-manager-rules-col">
                            <h4><FaRedo /> Regras Ativas</h4>
                            {recurrenceRules.length === 0 ? <p style={{fontStyle:'italic', color:'#999'}}>Nenhuma regra.</p> : (
                                <ul className="mood-manager-rules-list">
                                    {recurrenceRules.map(r => (
                                        <li key={r.id}>
                                            <span>{formatRule(r)}</span>
                                            <button onClick={() => deleteDocHandler('recurrenceRules', r.id)}><FaTrashAlt /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="mood-manager-rules-col">
                            <h4><FaCalendarCheck /> Agendamentos</h4>
                            {scheduledCampaigns.length === 0 ? <p style={{fontStyle:'italic', color:'#999'}}>Nada agendado.</p> : (
                                <ul className="mood-manager-rules-list">
                                    {scheduledCampaigns.map(c => (
                                        <li key={c.id}>
                                            <span>{new Date(c.scheduledFor.toDate()).toLocaleString()}</span>
                                            <button onClick={() => deleteDocHandler('moodSurveyCampaigns', c.id)}><FaTrashAlt /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* LISTA E FILTROS */}
            <div className="mood-manager-card no-padding">
                <div className="mood-manager-header-flex">
                    <div className="mood-manager-title-group">
                        <h3>Histórico de Respostas</h3>
                    </div>
                    <div className="mood-manager-filters">
                        <div className="mood-manager-search-box">
                            <FaSearch />
                            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        </div>
                        <div className="mood-manager-search-box">
                            <FaFilter />
                            <select value={filterMood} onChange={e => setFilterMood(e.target.value)} style={{paddingLeft: '36px'}}>
                                <option value="">Todos Humores</option>
                                <option value="muito-feliz">Muito Feliz</option>
                                <option value="feliz">Feliz</option>
                                <option value="neutro">Neutro</option>
                                <option value="triste">Triste</option>
                                <option value="muito-triste">Muito Triste</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="mood-manager-table-wrapper">
                    <table className="mood-manager-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Colaborador</th>
                                <th>Humor</th>
                                <th>Observação</th>
                                <th>Origem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(res => {
                                const mood = MOOD_CONFIG[res.humor] || { label: res.humor, color: '#333', bg: '#eee' };
                                const date = res.createdAt ? res.createdAt.toDate() : new Date();
                                return (
                                    <tr key={res.id}>
                                        <td style={{fontFamily:'monospace', color:'#666'}}>
                                            {date.toLocaleDateString('pt-BR')} <br/> <small>{date.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit'})}</small>
                                        </td>
                                        <td>
                                            <div className="mood-manager-user-cell">
                                                <div className="mood-manager-avatar">{res.isAnonymous ? '?' : (res.userName ? res.userName.charAt(0) : 'U')}</div>
                                                <div className="mood-manager-user-info">
                                                    <span className="name">{res.isAnonymous ? 'Anônimo' : res.userName}</span>
                                                    <span className="email">{!res.isAnonymous && res.userEmail}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="mood-manager-badge" style={{color: mood.color, backgroundColor: mood.bg}}>
                                                {mood.icon} {mood.label}
                                            </span>
                                        </td>
                                        <td style={{maxWidth:'250px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontStyle:'italic', color:'#666'}} title={res.observacao}>
                                            {res.observacao || '-'}
                                        </td>
                                        <td>
                                            <span className={`mood-manager-origin ${res.origem === 'publico' ? 'public' : 'platform'}`}>
                                                {res.origem === 'publico' ? 'Externo' : 'Interno'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            {currentItems.length === 0 && <tr><td colSpan="5" className="mood-manager-empty">Nenhum registro encontrado.</td></tr>}
                        </tbody>
                    </table>
                </div>
                
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} onItemsPerPageChange={setItemsPerPage} totalItems={filteredResponses.length} />
            </div>
        </div>
    );
};

export default MoodManagement;